import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  GrantAcquittalReport,
  GrantApplication,
  GrantDisbursementMilestone,
  GrantScheme,
  GrantScore,
  Page,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge } from '../../ui/components';

const SCHEMES: GrantScheme[] = ['SPORTS', 'ARTS', 'YOUTH'];
const STATUSES = ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'SCORED', 'REJECTED', 'DISBURSING', 'COMPLETED', 'WITHDRAWN'] as const;

function isMine(grant: GrantApplication, nin?: string): boolean {
  return grant.applicantType === 'PERSON' && Boolean(nin) && grant.applicantId === nin;
}

export function GrantsPage() {
  const { persona } = useAuth();
  const isPanel = persona?.roles.includes('GRANTS_PANEL') ?? false;
  const [status, setStatus] = useState('');
  const [scheme, setScheme] = useState('');
  const [q, setQ] = useState('');
  const [showSandbox, setShowSandbox] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = new URLSearchParams({ limit: '100' });
  if (status) query.set('status', status);
  if (scheme) query.set('scheme', scheme);
  if (q) query.set('q', q);
  const grantsQ = useApiQuery<Page<GrantApplication>>(isPanel ? `/api/v1/m30/grants?${query.toString()}` : null);
  const mineQ = useApiQuery<{ items: GrantApplication[] }>(persona?.nin ? '/api/v1/m30/grants/me' : null);

  const items = isPanel ? grantsQ.data?.items ?? [] : mineQ.data?.items ?? [];
  const selected = items.find((g) => g.id === selectedId) ?? null;
  const reloadAll = () => {
    grantsQ.reload();
    mineQ.reload();
  };

  return (
    <div>
      <PageHeader
        title="Grant Applications"
        subtitle="Full lifecycle — draft, submit, score, decide, disburse, acquit, complete — grants.ts + grantDisbursements.ts."
        actions={
          <button className="btn-secondary" onClick={() => setShowSandbox(true)}>
            + Start a test application
          </button>
        }
      />

      {!isPanel ? (
        <div className="mb-4">
          <ErrorNote message="Signed in without the Grants Panel role — showing only applications you started yourself below. Scoring, decisions, milestone release and acquittal review require GRANTS_PANEL." />
        </div>
      ) : (
        <div className="mb-3 flex flex-wrap gap-2">
          <input className="field-input max-w-xs" placeholder="Search project or applicant…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={scheme} onChange={(e) => setScheme(e.target.value)}>
            <option value="">All schemes</option>
            {SCHEMES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-5">
        <div className="lg:col-span-2">
          {(isPanel ? grantsQ.error : mineQ.error) ? <ErrorNote message={(isPanel ? grantsQ.error : mineQ.error)!} /> : null}
          <div className="card overflow-x-auto">
            {(isPanel ? grantsQ.loading : mineQ.loading) ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState title="No applications" hint={isPanel ? undefined : 'Use "Start a test application" to try the applicant journey yourself.'} />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Project</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((g) => (
                    <tr key={g.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === g.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(g.id)}>
                      <td className="table-cell">
                        <div className="font-medium">{g.projectTitle}</div>
                        <div className="text-[11px] text-slate-400">
                          {g.refNo} · {g.scheme} · {g.applicantName}
                        </div>
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={g.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          {selected ? (
            <GrantDetail key={selected.id} grant={selected} isPanel={isPanel} onChanged={reloadAll} />
          ) : (
            <SectionCard>
              <EmptyState title="Select an application" />
            </SectionCard>
          )}
        </div>
      </div>

      {showSandbox ? (
        <SandboxCreateModal
          onClose={() => setShowSandbox(false)}
          onDone={(id) => {
            setShowSandbox(false);
            reloadAll();
            setSelectedId(id);
          }}
        />
      ) : null}
    </div>
  );
}

function SandboxCreateModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const { persona } = useAuth();
  const [scheme, setScheme] = useState<GrantScheme>('YOUTH');
  const [applicantName, setApplicantName] = useState(persona?.name ?? '');
  const [projectTitle, setProjectTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requestedAmount, setRequestedAmount] = useState('10000');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<GrantApplication>('/api/v1/m30/grants', {
        scheme,
        applicantName,
        projectTitle,
        description,
        budgetJson: {},
        requestedAmount: Number.parseFloat(requestedAmount) || 0,
      });
      onDone(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Start a test application" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          m30 derives the applicant from your own signed-in NIN ({persona?.nin ?? '—'}) — there is no staff "on behalf of a
          citizen" field on this endpoint (see build report). This creates a real DRAFT application under your own identity
          so you can walk it through DRAFT→SUBMITTED yourself before the Grants Panel takes over.
        </p>
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Scheme">
          <select className="field-input" value={scheme} onChange={(e) => setScheme(e.target.value as GrantScheme)}>
            {SCHEMES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Applicant display name">
          <input className="field-input" value={applicantName} onChange={(e) => setApplicantName(e.target.value)} />
        </Field>
        <Field label="Project title">
          <input className="field-input" value={projectTitle} onChange={(e) => setProjectTitle(e.target.value)} placeholder="e.g. Praslin Youth Debate Club — seed funding" />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Requested amount (SCR)">
          <input className="field-input" type="number" min="1" value={requestedAmount} onChange={(e) => setRequestedAmount(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || projectTitle.trim().length < 2 || description.trim().length < 10}>
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GrantDetail({ grant, isPanel, onChanged }: { grant: GrantApplication; isPanel: boolean; onChanged: () => void }) {
  const { persona } = useAuth();
  const mine = isMine(grant, persona?.nin);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scoresQ = useApiQuery<{ items: GrantScore[] }>(isPanel ? `/api/v1/m30/grants/${grant.id}/scores` : null);
  const milestonesQ = useApiQuery<{ items: GrantDisbursementMilestone[] }>(
    isPanel || mine ? `/api/v1/m30/grants/${grant.id}/milestones` : null,
  );
  const acquittalsQ = useApiQuery<{ items: GrantAcquittalReport[] }>(
    isPanel || mine ? `/api/v1/m30/grants/${grant.id}/acquittals` : null,
  );

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      scoresQ.reload();
      milestonesQ.reload();
      acquittalsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const alreadyScored = (scoresQ.data?.items ?? []).some((s) => s.panelistId === persona?.sub);

  return (
    <div className="space-y-4">
      <SectionCard
        title={grant.projectTitle}
        actions={
          <div className="flex items-center gap-1.5">
            <StatusBadge status={grant.status} />
          </div>
        }
      >
        {error ? (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">Reference</dt>
            <dd className="font-mono text-xs font-medium text-slate-800">{grant.refNo}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Scheme</dt>
            <dd className="font-medium text-slate-800">{grant.scheme}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Applicant</dt>
            <dd className="font-medium text-slate-800">
              {grant.applicantName} ({grant.applicantType})
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Requested / Approved</dt>
            <dd className="font-medium text-slate-800">
              {scr(grant.requestedAmount)} / {scr(grant.approvedAmount)}
            </dd>
          </div>
          {grant.scoreAverage !== null ? (
            <div className="flex justify-between">
              <dt className="text-slate-500">Panel average score</dt>
              <dd className="font-medium text-slate-800">{grant.scoreAverage.toFixed(1)} / 100</dd>
            </div>
          ) : null}
          {grant.panelDecisionReason ? (
            <div>
              <dt className="text-slate-500">Panel decision note</dt>
              <dd className="mt-0.5 text-xs text-slate-600">{grant.panelDecisionReason}</dd>
            </div>
          ) : null}
          {grant.withdrawnReason ? (
            <div>
              <dt className="text-slate-500">Withdrawal reason</dt>
              <dd className="mt-0.5 text-xs text-slate-600">{grant.withdrawnReason}</dd>
            </div>
          ) : null}
        </dl>
        <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-600">{grant.description}</p>
        {grant.agreementDocRef ? <p className="mt-2 text-[11px] text-slate-400">Agreement doc: {grant.agreementDocRef}</p> : null}
        {grant.completionCertRef ? <p className="text-[11px] text-slate-400">Completion certificate: {grant.completionCertRef}</p> : null}

        <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
          {/* Applicant actions (isSelfApplicant) */}
          {mine && grant.status === 'DRAFT' ? (
            <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => run(() => api.post(`/api/v1/m30/grants/${grant.id}/submit`))} disabled={busy}>
              Submit application
            </button>
          ) : null}
          {mine && (['DRAFT', 'SUBMITTED', 'UNDER_REVIEW'] as string[]).includes(grant.status) ? (
            <button
              className="btn-secondary !px-2.5 !py-1 text-xs"
              onClick={() => {
                const reason = prompt('Withdrawal reason:');
                if (reason) run(() => api.post(`/api/v1/m30/grants/${grant.id}/withdraw`, { reason }));
              }}
              disabled={busy}
            >
              Withdraw
            </button>
          ) : null}

          {/* Panel actions */}
          {isPanel && grant.status === 'SUBMITTED' ? (
            <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => run(() => api.post(`/api/v1/m30/grants/${grant.id}/claim`))} disabled={busy}>
              Claim for review
            </button>
          ) : null}
          {isPanel && (['UNDER_REVIEW', 'SCORED'] as string[]).includes(grant.status) ? (
            <button
              className="btn-danger !px-2.5 !py-1 text-xs"
              onClick={() => {
                const reason = prompt('Rejection reason:');
                if (reason) run(() => api.post(`/api/v1/m30/grants/${grant.id}/decide`, { outcome: 'REJECTED', reason }));
              }}
              disabled={busy}
            >
              Reject
            </button>
          ) : null}
          {isPanel && grant.status === 'UNDER_REVIEW' ? (
            <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => run(() => api.post(`/api/v1/m30/grants/${grant.id}/finalize-scoring`))} disabled={busy}>
              Finalize scoring
            </button>
          ) : null}
          {isPanel && grant.status === 'SCORED' ? (
            <ApproveButton requestedAmount={grant.requestedAmount} busy={busy} onApprove={(amount, reason) => run(() => api.post(`/api/v1/m30/grants/${grant.id}/decide`, { outcome: 'APPROVED', approvedAmount: amount, reason }))} />
          ) : null}
        </div>
      </SectionCard>

      {isPanel ? (
        <SectionCard title="Panel scores">
          {scoresQ.loading ? (
            <Spinner label="Loading…" />
          ) : scoresQ.error ? (
            <ErrorNote message={scoresQ.error} />
          ) : (scoresQ.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">No scores recorded yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {scoresQ.data!.items.map((s) => (
                <li key={s.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5">
                  <span>
                    {s.panelistId === persona?.sub ? <strong>You</strong> : <span className="text-slate-500">{s.panelistId}</span>}
                    {s.comment ? <span className="ml-2 text-xs text-slate-400">"{s.comment}"</span> : null}
                  </span>
                  <span className="tabular-nums font-semibold">{s.score.toFixed(0)} / 100</span>
                </li>
              ))}
            </ul>
          )}
          {grant.status === 'UNDER_REVIEW' ? (
            alreadyScored ? (
              <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-400">You have already scored this application.</p>
            ) : (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <ScoreForm onSubmit={(score, comment) => run(() => api.post(`/api/v1/m30/grants/${grant.id}/scores`, { score, comment }))} busy={busy} />
              </div>
            )
          ) : null}
        </SectionCard>
      ) : null}

      {isPanel || mine ? (
        <SectionCard title="Disbursement milestones">
          {milestonesQ.loading ? (
            <Spinner label="Loading…" />
          ) : milestonesQ.error ? (
            <ErrorNote message={milestonesQ.error} />
          ) : (milestonesQ.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">No milestones yet — created automatically once the panel approves.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {milestonesQ.data!.items.map((m) => (
                <li key={m.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5">
                  <span>
                    #{m.sequenceNo} {m.description} — <span className="tabular-nums font-semibold">{scr(m.amount)}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={m.status} />
                    {isPanel && grant.status === 'DISBURSING' && m.status === 'PENDING' ? (
                      <button
                        className="btn-success !px-2 !py-1 text-xs"
                        onClick={() => run(() => api.post(`/api/v1/m30/grants/${grant.id}/milestones/${m.id}/release`))}
                        disabled={busy}
                      >
                        Release
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      ) : null}

      {isPanel || mine ? (
        <SectionCard title="Acquittal reports">
          {acquittalsQ.loading ? (
            <Spinner label="Loading…" />
          ) : acquittalsQ.error ? (
            <ErrorNote message={acquittalsQ.error} />
          ) : (acquittalsQ.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">No acquittal reports submitted yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {acquittalsQ.data!.items.map((a) => (
                <li key={a.id} className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{scr(a.amountSpent)} spent{a.milestoneId ? ' (milestone)' : ' (overall)'}</span>
                    <StatusBadge status={a.status} />
                  </div>
                  <p className="mt-1 text-xs text-slate-600">{a.narrative}</p>
                  {isPanel && a.status === 'SUBMITTED' ? (
                    <div className="mt-2 flex gap-1.5">
                      <button className="btn-success !px-2 !py-1 text-xs" onClick={() => run(() => api.post(`/api/v1/m30/grants/${grant.id}/acquittals/${a.id}/review`, { outcome: 'APPROVED' }))} disabled={busy}>
                        Approve
                      </button>
                      <button
                        className="btn-secondary !px-2 !py-1 text-xs"
                        onClick={() => {
                          const notes = prompt('What needs clarifying?') ?? undefined;
                          run(() => api.post(`/api/v1/m30/grants/${grant.id}/acquittals/${a.id}/review`, { outcome: 'QUERIED', notes }));
                        }}
                        disabled={busy}
                      >
                        Query
                      </button>
                      <button
                        className="btn-danger !px-2 !py-1 text-xs"
                        onClick={() => {
                          const notes = prompt('Rejection notes:') ?? undefined;
                          run(() => api.post(`/api/v1/m30/grants/${grant.id}/acquittals/${a.id}/review`, { outcome: 'REJECTED', notes }));
                        }}
                        disabled={busy}
                      >
                        Reject
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {mine && (['DISBURSING', 'COMPLETED'] as string[]).includes(grant.status) ? (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <AcquittalForm
                milestones={milestonesQ.data?.items ?? []}
                busy={busy}
                onSubmit={(input) => run(() => api.post(`/api/v1/m30/grants/${grant.id}/acquittals`, input))}
              />
            </div>
          ) : null}
        </SectionCard>
      ) : null}
    </div>
  );
}

function ScoreForm({ onSubmit, busy }: { onSubmit: (score: number, comment?: string) => void; busy: boolean }) {
  const [score, setScore] = useState('75');
  const [comment, setComment] = useState('');
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Score (0-100)">
          <input className="field-input" type="number" min="0" max="100" value={score} onChange={(e) => setScore(e.target.value)} />
        </Field>
        <Field label="Comment (optional)">
          <input className="field-input" value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary w-full" onClick={() => onSubmit(Number.parseFloat(score), comment || undefined)} disabled={busy}>
        Submit score
      </button>
    </div>
  );
}

function ApproveButton({ requestedAmount, busy, onApprove }: { requestedAmount: number; busy: boolean; onApprove: (amount: number, reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(requestedAmount));
  const [reason, setReason] = useState('Approved by panel decision');

  if (!open) {
    return (
      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => setOpen(true)}>
        Approve…
      </button>
    );
  }
  return (
    <div className="w-full space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Approved amount (SCR)">
          <input className="field-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <div className="flex justify-end gap-1.5">
        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button
          className="btn-success !px-2.5 !py-1 text-xs"
          onClick={() => onApprove(Number.parseFloat(amount), reason)}
          disabled={busy || !amount || Number.parseFloat(amount) <= 0}
        >
          Confirm approval
        </button>
      </div>
    </div>
  );
}

function AcquittalForm({
  milestones,
  busy,
  onSubmit,
}: {
  milestones: GrantDisbursementMilestone[];
  busy: boolean;
  onSubmit: (input: { milestoneId?: string; narrative: string; amountSpent: number; documentRef?: string }) => void;
}) {
  const [milestoneId, setMilestoneId] = useState('');
  const [narrative, setNarrative] = useState('');
  const [amountSpent, setAmountSpent] = useState('');

  return (
    <div className="space-y-2">
      <Field label="Against milestone (blank = overall acquittal)">
        <select className="field-input" value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}>
          <option value="">Overall acquittal</option>
          {milestones.map((m) => (
            <option key={m.id} value={m.id}>
              #{m.sequenceNo} {m.description} ({scr(m.amount)})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Narrative">
        <textarea className="field-input" rows={2} value={narrative} onChange={(e) => setNarrative(e.target.value)} placeholder="How was the money spent?" />
      </Field>
      <Field label="Amount spent (SCR)">
        <input className="field-input" type="number" min="1" value={amountSpent} onChange={(e) => setAmountSpent(e.target.value)} />
      </Field>
      <button
        className="btn-primary w-full"
        onClick={() =>
          onSubmit({
            milestoneId: milestoneId || undefined,
            narrative,
            amountSpent: Number.parseFloat(amountSpent) || 0,
          })
        }
        disabled={busy || narrative.trim().length < 10 || !amountSpent}
      >
        Submit acquittal report
      </button>
    </div>
  );
}
